import type { ButtonHTMLAttributes, HTMLAttributes, ReactNode } from "react";
import { LoaderCircle } from "lucide-react";
import { cn } from "../lib/utils";

export function Button({ className, variant = "primary", loading, children, ...props }: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: "primary" | "secondary" | "ghost" | "danger"; loading?: boolean }) {
  const styles = {
    primary: "bg-[var(--accent)] text-white border-transparent hover:brightness-105",
    secondary: "bg-[var(--surface-muted)] text-[var(--text)] border-[var(--border)] hover:bg-[var(--accent-soft)]",
    ghost: "bg-transparent text-[var(--text-secondary)] border-transparent hover:bg-[var(--surface-muted)] hover:text-[var(--text)]",
    danger: "bg-[var(--red-soft)] text-[var(--red)] border-transparent hover:brightness-95",
  };
  return <button className={cn("inline-flex h-9 items-center justify-center gap-2 rounded-xl border px-3.5 text-[13px] font-semibold transition disabled:pointer-events-none disabled:opacity-50", styles[variant], className)} disabled={loading || props.disabled} {...props}>
    {loading && <LoaderCircle size={15} className="animate-spin" />} {children}
  </button>;
}

export function Card({ className, children, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("surface rounded-[20px]", className)} {...props}>{children}</div>;
}

export function Badge({ tone = "neutral", children, className }: { tone?: "neutral" | "blue" | "green" | "orange" | "red" | "purple"; children: ReactNode; className?: string }) {
  const tones = {
    neutral: "bg-[var(--surface-muted)] text-[var(--text-secondary)]",
    blue: "bg-[var(--accent-soft)] text-[var(--accent)]",
    green: "bg-[var(--green-soft)] text-[var(--green)]",
    orange: "bg-[var(--orange-soft)] text-[var(--orange)]",
    red: "bg-[var(--red-soft)] text-[var(--red)]",
    purple: "bg-purple-500/10 text-[var(--purple)]",
  };
  return <span className={cn("inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold leading-none whitespace-nowrap", tones[tone], className)}>{children}</span>;
}

export function PageHeader({ title, description, actions }: { title: string; description?: string; actions?: ReactNode }) {
  return <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
    <div><h1 className="text-[28px] font-bold tracking-[-0.035em] sm:text-[32px]">{title}</h1>{description && <p className="mt-1.5 max-w-2xl text-sm leading-6 text-[var(--text-secondary)]">{description}</p>}</div>
    {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
  </div>;
}

export function Skeleton({ className }: { className?: string }) {
  return <div aria-hidden="true" className={cn("skeleton-shimmer rounded-lg bg-[var(--surface-muted)]", className)} />;
}

export function LoadingState({ label, className }: { label: string; className?: string }) {
  return <div role="status" aria-live="polite" className={cn("flex min-h-52 flex-col items-center justify-center px-6 py-10 text-center", className)}>
    <div className="relative mb-4 grid size-12 place-items-center rounded-2xl bg-[var(--accent-soft)] text-[var(--accent)]">
      <span className="absolute inset-0 animate-ping rounded-2xl bg-[var(--accent-soft)] opacity-50" />
      <LoaderCircle size={20} className="relative animate-spin" />
    </div>
    <p className="text-sm font-semibold text-[var(--text-secondary)]">{label}</p>
  </div>;
}

export function ListSkeleton({ rows = 5 }: { rows?: number }) {
  return <div className="divide-y divide-[var(--border)]" aria-hidden="true">
    {Array.from({ length: rows }, (_, index) => <div key={index} className="flex items-center gap-4 px-5 py-4">
      <Skeleton className="size-10 shrink-0 rounded-xl" />
      <div className="min-w-0 flex-1 space-y-2"><Skeleton className="h-3 w-1/3" /><Skeleton className="h-2.5 w-2/3" /></div>
      <div className="hidden gap-3 sm:flex"><Skeleton className="h-8 w-16" /><Skeleton className="h-8 w-20" /></div>
    </div>)}
  </div>;
}

export function CardGridSkeleton({ cards = 6 }: { cards?: number }) {
  return <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" aria-hidden="true">
    {Array.from({ length: cards }, (_, index) => <Card key={index} className="space-y-4 p-5"><div className="flex justify-between"><Skeleton className="size-10 rounded-xl" /><Skeleton className="h-6 w-20 rounded-full" /></div><Skeleton className="h-4 w-2/3" /><Skeleton className="h-3 w-1/2" /><Skeleton className="h-px w-full" /><Skeleton className="h-3 w-full" /><Skeleton className="h-3 w-4/5" /></Card>)}
  </div>;
}

export function EmptyState({ icon, title, detail }: { icon: ReactNode; title: string; detail?: string }) {
  return <div className="flex min-h-52 flex-col items-center justify-center px-6 py-10 text-center">
    <div className="mb-4 grid size-12 place-items-center rounded-2xl bg-[var(--surface-muted)] text-[var(--text-secondary)]">{icon}</div>
    <p className="font-semibold">{title}</p>{detail && <p className="mt-1 max-w-sm text-sm text-[var(--text-secondary)]">{detail}</p>}
  </div>;
}

export function Segmented<T extends string | number>({ value, options, onChange, ariaLabel }: { value: T; options: Array<{ value: T; label: string; icon?: ReactNode }>; onChange: (value: T) => void; ariaLabel: string }) {
  return <div role="group" aria-label={ariaLabel} className="inline-flex rounded-xl bg-[var(--surface-muted)] p-1">
    {options.map((option) => <button key={option.value} onClick={() => onChange(option.value)} className={cn("flex h-8 items-center gap-1.5 rounded-lg px-3 text-xs font-semibold text-[var(--text-secondary)] transition", value === option.value && "bg-[var(--surface-solid)] text-[var(--text)] shadow-sm")} aria-pressed={value === option.value}>{option.icon}{option.label}</button>)}
  </div>;
}

export function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return <label className="block"><span className="mb-2 block text-xs font-semibold text-[var(--text-secondary)]">{label}</span>{children}{hint && <span className="mt-1.5 block text-xs text-[var(--text-tertiary)]">{hint}</span>}</label>;
}

export const inputClass = "h-10 w-full rounded-xl border border-[var(--border)] bg-[var(--surface-muted)] px-3 text-sm text-[var(--text)] outline-none transition placeholder:text-[var(--text-tertiary)] focus:border-[var(--accent)] focus:bg-[var(--surface-solid)]";
